import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { visibleModules, leckeszamSzintenkent } from '../lib/courseOutline';
import TrialStrip from '../components/TrialStrip';
import Navbar from '../components/Navbar';
import LockedModule from '../components/LockedModule';
import UserStats from '../components/UserStats';
import ActivityCalendar from '../components/ActivityCalendar';
import QuizSection from '../components/QuizSection';
import ModuleResources from '../components/ModuleResources';
import { loadModuleResources } from '../lib/moduleResources';
import GyakorloLink from '../components/gyakorlo/GyakorloLink';
import AlakzatLink from '../components/alakzat/AlakzatLink';

const CoursePage = ({ session, access, profile, onAccessChange }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [expandedModules, setExpandedModules] = useState([]);
  const [completedIds, setCompletedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lastViewed, setLastViewed] = useState(null);
  const [activityData, setActivityData] = useState({});
  const [streakDays, setStreakDays] = useState(0);
  const [yearlyCount, setYearlyCount] = useState(0);
  const [quizzesByModule, setQuizzesByModule] = useState({});
  const [attemptsByQuiz, setAttemptsByQuiz] = useState({});

  const loadProgress = useCallback(async () => {
    const { data } = await supabase
      .from('user_progress')
      .select('lesson_id')
      .eq('user_id', session.user.id)
      .not('completed_at', 'is', null);
    setCompletedIds((data || []).map(p => p.lesson_id));
  }, [session.user.id]);

  const [resourcesByModule, setResourcesByModule] = useState({});

  const loadCourse = useCallback(async () => {
    const { data: courseData, error: courseError } = await supabase
      .from('courses')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();
    console.log('Course:', courseData, courseError);
    if (!courseData) { navigate('/tanfolyamok'); return; }
    setCourse(courseData);

    const { data: modulesData, error: modulesError } = await supabase
      .from('modules')
      .select('*')
      .eq('course_id', courseData.id)
      .order('order_index');
    console.log('Modules:', modulesData, modulesError);

    // Leckék a course_outline RPC-ből (Plusz 2. kör): minden publikált lecke
    // metaadata (cím, hossz, sorrend, „hamarosan”), és hogy a hívó olvashatja-e;
    // a zárt leckék tartalma (videó, leírás) nem jön le. Egy hívás az egész kurzusra.
    const { data: outlineRows, error: outlineError } = await supabase
      .rpc('course_outline', { p_course: courseData.id });
    if (outlineError) console.warn('course_outline:', outlineError.message);
    const byModule = {};
    (outlineRows || []).forEach(r => {
      (byModule[r.module_id] ||= []).push({
        id: r.lesson_id, title: r.title, duration_minutes: r.duration_minutes, order_index: r.order_index,
        thumbnail_url: r.thumbnail_url, coming_soon: r.coming_soon, can_read: r.can_read,
        required_level: r.required_level, published: true,
      });
    });
    const loadedModules = (modulesData || []).map(module => {
      const lessons = byModule[module.id] || [];
      return { ...module, lessons, locked: lessons.length > 0 && lessons.every(l => !l.can_read) };
    });
    // Hidden lessons and modules left without any are not shown (courseOutline.js).
    const modulesWithLessons = visibleModules(loadedModules);

    setModules(modulesWithLessons);
    // Segédletek a modulokhoz (Plusz-segédletek 1. kör); hiba esetén üres.
    loadModuleResources(courseData.id).then(setResourcesByModule);
    const firstOpen = modulesWithLessons.find(m => !m.locked) || modulesWithLessons[0];
    if (firstOpen) {
      setExpandedModules([firstOpen.id]);
    }

    // Modul kvízek + a felhasználó legutóbbi próbálkozása. Két lekérdezés az
    // összes modulra, hogy a modulok száma ne befolyásolja a kérések számát.
    const moduleIds = modulesWithLessons.map(m => m.id);
    if (moduleIds.length) {
      const { data: quizRows } = await supabase
        .from('quizzes')
        .select('*')
        .in('module_id', moduleIds);

      const byModule = {};
      (quizRows || []).forEach(q => { byModule[q.module_id] = q; });
      setQuizzesByModule(byModule);

      const quizIds = (quizRows || []).map(q => q.id);
      if (quizIds.length) {
        const { data: attemptRows } = await supabase
          .from('quiz_attempts')
          .select('*')
          .eq('user_id', session.user.id)
          .in('quiz_id', quizIds)
          .order('completed_at', { ascending: false });

        const latest = {};
        (attemptRows || []).forEach(a => {
          if (!latest[a.quiz_id]) latest[a.quiz_id] = a;
        });
        setAttemptsByQuiz(latest);
      } else {
        setAttemptsByQuiz({});
      }
    } else {
      setQuizzesByModule({});
      setAttemptsByQuiz({});
    }

    // Utoljára megnézett lecke ebben a kurzusban
    const allLessonIds = modulesWithLessons.flatMap(m => m.lessons.filter(l => l.can_read).map(l => l.id));
    if (allLessonIds.length) {
      const { data: lastProg } = await supabase
        .from('user_progress')
        .select('lesson_id, updated_at')
        .eq('user_id', session.user.id)
        .in('lesson_id', allLessonIds)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      const lObj = lastProg?.lesson_id
        ? modulesWithLessons.flatMap(m => m.lessons).find(l => l.id === lastProg.lesson_id)
        : null;
      setLastViewed(lObj ? { id: lObj.id, title: lObj.title } : null);
    } else {
      setLastViewed(null);
    }

    // Aktivitás (streak naptár) — utolsó 364 nap
    const { data: activityRows } = await supabase
      .from('user_progress')
      .select('completed_at')
      .eq('user_id', session.user.id)
      .not('completed_at', 'is', null)
      .gte('completed_at', new Date(Date.now() - 364 * 24 * 60 * 60 * 1000).toISOString());

    const counts = {};
    (activityRows || []).forEach(row => {
      const day = row.completed_at.split('T')[0];
      counts[day] = (counts[day] || 0) + 1;
    });
    setActivityData(counts);
    setYearlyCount(activityRows?.length || 0);

    let streak = 0;
    const todayD = new Date();
    for (let i = 0; i < 365; i++) {
      const d = new Date(todayD);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().split('T')[0];
      if (counts[key]) streak++;
      else if (i > 0) break;
    }
    setStreakDays(streak);

    await loadProgress();
    setLoading(false);
  }, [slug, navigate, loadProgress, session.user.id]);

  useEffect(() => { loadCourse(); }, [loadCourse]);

  // SEO: védett oldal — ne indexelje a Google
  useEffect(() => {
    const robots = document.querySelector('meta[name="robots"]');
    const prevRobots = robots?.getAttribute('content');
    const prevTitle = document.title;
    if (robots) robots.setAttribute('content', 'noindex, nofollow');
    return () => {
      if (robots) robots.setAttribute('content', prevRobots || 'index, follow');
      document.title = prevTitle;
    };
  }, []);

  useEffect(() => {
    if (course?.title) document.title = `${course.title} — Web3 Kurzus Egyetem`;
  }, [course]);

  // Mobile breakpoint tracking (768px)
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleModule = (moduleId) => {
    setExpandedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const downloadCertificate = async () => {
    // jsPDF lazy betöltése — csak kattintáskor, hogy ne hizlalja a fő bundle-t
    const { jsPDF } = await import('jspdf');

    const userName = session.user.user_metadata?.full_name || session.user.email || 'Tanuló';
    const dateStr = new Date().toLocaleDateString('hu-HU');

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const cx = W / 2;

    // Háttér (fekete)
    doc.setFillColor(7, 7, 11);
    doc.rect(0, 0, W, H, 'F');

    // Teal keret
    doc.setDrawColor(0, 210, 200);
    doc.setLineWidth(3);
    doc.rect(24, 24, W - 48, H - 48);
    doc.setLineWidth(0.5);
    doc.rect(34, 34, W - 68, H - 68);

    // Fejléc
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 210, 200);
    doc.setFontSize(15);
    doc.text('WEB3 KURZUS EGYETEM', cx, 92, { align: 'center' });

    // Cím
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(34);
    doc.text('ELVÉGZÉSI TANÚSÍTVÁNY', cx, 162, { align: 'center' });

    doc.setDrawColor(0, 210, 200);
    doc.setLineWidth(1.5);
    doc.line(cx - 90, 184, cx + 90, 184);

    // Címzett
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(180, 180, 190);
    doc.setFontSize(13);
    doc.text('Ezt a tanúsítványt kapja:', cx, 240, { align: 'center' });

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(26);
    doc.text(userName, cx, 278, { align: 'center', maxWidth: W - 140 });

    // Kurzus
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(180, 180, 190);
    doc.setFontSize(13);
    doc.text('Sikeresen elvégezte az alábbi tanfolyamot:', cx, 322, { align: 'center' });

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 210, 200);
    doc.setFontSize(20);
    doc.text(course.title, cx, 354, { align: 'center', maxWidth: W - 140 });

    // Dátum
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 150, 160);
    doc.setFontSize(12);
    doc.text(`Befejezés dátuma: ${dateStr}`, cx, 408, { align: 'center' });

    // Aláírás
    doc.setDrawColor(120, 120, 130);
    doc.setLineWidth(0.5);
    doc.line(cx - 110, H - 96, cx + 110, H - 96);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.text('Dr. Janó Dániel', cx, H - 76, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 150, 160);
    doc.setFontSize(11);
    doc.text('Web3 Kurzus', cx, H - 60, { align: 'center' });

    const slug = (course.title || 'kurzus')
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    doc.save(`web3kurzus-${slug}-tanusitvany.pdf`);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f1117' }}>
        <Navbar access={access} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
          <p style={{ color: 'rgba(255,255,255,0.4)' }}>Betöltés...</p>
        </div>
      </div>
    );
  }

  // The Podia legacy course row is readable by everyone once published, but its
  // lessons only by OG users (lessons_select). Without this, a non-OG user
  // opening the link directly would get the module list with nothing in it.
  if (course?.required_tier === 'og' && access && access.is_og !== true) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f1117' }}>
        <Navbar access={access} />
        <div style={{
          maxWidth: 480, margin: '0 auto', padding: '96px 24px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, textAlign: 'center',
        }}>
          <h1 style={{ color: '#fff', fontSize: 24, fontWeight: 700, margin: 0 }}>{course.title}</h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 15, lineHeight: 1.6, margin: 0 }}>
            Ez a tanfolyam a korábbi Podia-vásárlók számára érhető el.
          </p>
          <button
            type="button"
            onClick={() => navigate('/tanfolyamok')}
            style={{
              background: '#00D2C8', color: '#07070b', border: 'none', borderRadius: 10,
              padding: '12px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}
          >
            ← Vissza a tanfolyamokhoz
          </button>
        </div>
      </div>
    );
  }

  // A „Kezdés / Folytatás” és a haladás az olvasható leckékhez mér (FREE-nél a
  // 35 nyitott lecke a 100%); a tanúsítvány a kurzus összes leckéje után jár.
  // A szintenkénti leckeszám a course_outline-ból (Tananyag 1. kör: nem beégetve).
  const szintenkent = leckeszamSzintenkent(modules);
  const allLessons = modules.flatMap(m => m.lessons.filter(l => l.can_read));
  const courseLessons = modules.reduce((sum, m) => sum + m.lessons.length, 0);
  const totalLessons = allLessons.length;
  const completedCount = allLessons.filter(l => completedIds.includes(l.id)).length;
  const progressPercent = totalLessons > 0
    ? Math.round((completedCount / totalLessons) * 100) : 0;
  const courseDone = courseLessons > 0 && completedCount === courseLessons;

  return (
    <div style={{ minHeight: '100vh', background: '#0f1117' }}>
      <Navbar access={access} />

      <div style={isMobile
        ? { display: 'block' }
        : { display: 'flex', height: 'calc(100vh - 60px)' }
      }>
        {/* Sidebar */}
        <aside style={isMobile
          ? {
              position: 'fixed', top: 0, left: 0, bottom: 0,
              width: '100%', zIndex: 1000,
              background: '#0a0a0f',
              borderRight: '1px solid rgba(255,255,255,0.06)',
              overflowY: 'auto', padding: '20px 0',
              transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
              transition: 'transform 0.25s ease',
            }
          : {
              width: '300px', minWidth: '300px',
              background: '#0a0a0f',
              borderRight: '1px solid rgba(255,255,255,0.06)',
              overflowY: 'auto', padding: '20px 0',
            }
        }>
          {isMobile && (
            <div style={{ padding: '0 16px 12px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSidebarOpen(false)}
                style={{
                  background: 'none', border: 'none',
                  color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
                  fontSize: 15, fontWeight: 600,
                }}
              >
                ✕ Bezárás
              </button>
            </div>
          )}
          <UserStats profile={profile} session={session} />
          <div style={{ padding: '0 16px 20px' }}>
            <button
              onClick={() => navigate('/tanfolyamok')}
              style={{
                background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)',
                cursor: 'pointer', fontSize: '13px', padding: '0',
                marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '4px',
              }}
            >
              ← Vissza
            </button>
            <h2 style={{ color: 'white', margin: '0 0 12px', fontSize: '15px', fontWeight: 700 }}>
              {course.title}
            </h2>

            {/* Progress bar */}
            <div style={{
              background: 'rgba(255,255,255,0.08)',
              borderRadius: '100px', height: '6px',
              marginBottom: '6px',
            }}>
              <div style={{
                background: 'linear-gradient(90deg, #00D2C8, #8C50FF)',
                width: `${progressPercent}%`,
                height: '100%', borderRadius: '100px',
                transition: 'width 0.3s ease',
              }} />
            </div>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', margin: 0 }}>
              {completedCount}/{totalLessons} lecke teljesítve · {progressPercent}%
            </p>
          </div>

          {/* Module list */}
          <div style={{ padding: '0 8px' }}>
            {modules.map(module => (
              <div key={module.id} style={{ marginBottom: '4px' }}>
                {/* Module header */}
                <div
                  onClick={() => toggleModule(module.id)}
                  style={{
                    padding: '10px 12px',
                    color: 'white', fontWeight: 700,
                    fontSize: '13px', letterSpacing: '0.02em',
                    background: 'rgba(255,255,255,0.04)',
                    borderRadius: '8px', marginBottom: '2px',
                    cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ flex: 1, marginRight: '8px', color: module.locked ? 'rgba(255,255,255,0.55)' : undefined }}>
                    {module.locked && <span aria-label="Zárt modul">🔒 </span>}{module.displayIndex}. {module.title}
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', whiteSpace: 'nowrap' }}>
                    {module.lessons.filter(l => completedIds.includes(l.id)).length}/{module.lessons.length}
                    {' '}{expandedModules.includes(module.id) ? '▲' : '▼'}
                  </span>
                </div>

                {/* Lessons */}
                {expandedModules.includes(module.id) && module.lessons.map(lesson => (
                  <div
                    key={lesson.id}
                    onClick={() => { navigate(`/lecke/${lesson.id}`); setSidebarOpen(false); }}
                    style={{
                      padding: '7px 12px 7px 28px',
                      cursor: 'pointer', fontSize: '12px',
                      color: completedIds.includes(lesson.id) ? '#00D2C8' : 'rgba(255,255,255,0.55)',
                      background: 'transparent',
                      borderRadius: '6px',
                      display: 'flex', alignItems: 'center', gap: '8px',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ fontSize: '11px', flexShrink: 0 }}>
                      {!lesson.can_read ? '🔒' : completedIds.includes(lesson.id) ? '✓' : '○'}
                    </span>
                    <span style={{ flex: 1, lineHeight: 1.4 }}>{lesson.title}</span>
                    {lesson.duration_minutes && (
                      <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '10px', marginLeft: 'auto', flexShrink: 0 }}>
                        {lesson.duration_minutes}p
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </aside>

        {/* Main content */}
        <main style={isMobile
          ? { width: '100%', overflowY: 'visible', padding: '20px 16px' }
          : { flex: 1, overflowY: 'auto', padding: '40px 48px' }
        }>
          <div style={{ maxWidth: '720px' }}>
            {course.thumbnail_url && (
              <img
                src={course.thumbnail_url}
                alt={course.title}
                style={{ width: '100%', borderRadius: '16px', marginBottom: '32px', maxHeight: '300px', objectFit: 'cover' }}
              />
            )}

            <h1 style={{ color: 'white', fontSize: '28px', fontWeight: 700, margin: '0 0 12px' }}>
              {course.title}
            </h1>

            {isMobile && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                <button
                  onClick={() => navigate('/tanfolyamok')}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: 'rgba(255,255,255,0.8)',
                    borderRadius: 8, padding: '8px 14px',
                    fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  ← Vissza
                </button>
                <button
                  onClick={() => setSidebarOpen(true)}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: 'rgba(255,255,255,0.8)',
                    borderRadius: 8, padding: '8px 14px',
                    fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  ☰ Modulok
                </button>
              </div>
            )}

            {course.description && (
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '15px', lineHeight: 1.7, margin: '0 0 32px' }}>
                {course.description}
              </p>
            )}

            {/* Plusz 2. kör: a Kripto Start szintenként nyílik (SZINTMATRIX) */}
            {course.required_tier === 'affiliate' && (
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', lineHeight: 1.6, margin: '-20px 0 28px' }}>
                {szintenkent.free}{'\u00a0'}lecke ingyen · {szintenkent.tag} TAG-gal · mind a {szintenkent.osszes} Plusszal
              </p>
            )}

            <TrialStrip session={session} access={access} onStarted={onAccessChange} />

            {/* Utoljára megnézett */}
            {lastViewed && (
              <div
                onClick={() => navigate(`/lecke/${lastViewed.id}`)}
                style={{
                  background: 'rgba(0,210,200,0.06)',
                  border: '1px solid rgba(0,210,200,0.2)',
                  borderRadius: 12, padding: '12px 16px',
                  marginBottom: 24, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 10,
                  transition: 'border-color 0.15s, background 0.15s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = 'rgba(0,210,200,0.4)';
                  e.currentTarget.style.background = 'rgba(0,210,200,0.1)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = 'rgba(0,210,200,0.2)';
                  e.currentTarget.style.background = 'rgba(0,210,200,0.06)';
                }}
              >
                <span style={{ color: '#00D2C8', fontSize: 16, flexShrink: 0 }}>▶</span>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, flex: 1, minWidth: 0 }}>
                  Folytasd ahol abbahagytad:{' '}
                  <span style={{ color: '#fff', fontWeight: 600 }}>{lastViewed.title}</span>
                </span>
                <span style={{ color: '#00D2C8', flexShrink: 0 }}>→</span>
              </div>
            )}

            {/* Stats */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '40px', flexWrap: 'wrap' }}>
              {[
                totalLessons < courseLessons
                  ? { label: 'Elérhető lecke', value: `${totalLessons}/${courseLessons}` }
                  : { label: 'Leckék', value: totalLessons },
                { label: 'Modulok', value: modules.length },
                { label: 'Teljesítve', value: completedCount },
                { label: 'Haladás', value: `${progressPercent}%` },
              ].map(stat => (
                <div key={stat.label} style={{
                  background: '#1a1d2e',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '12px', padding: '16px 20px',
                  flex: '1 1 120px', textAlign: 'center',
                }}>
                  <div style={{ color: '#00D2C8', fontSize: '22px', fontWeight: 700 }}>
                    {stat.value}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginTop: '4px' }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Certificate (kurzus teljesítve) */}
            {courseDone && (
              <div style={{
                background: 'rgba(0,210,200,0.06)',
                border: '1px solid rgba(0,210,200,0.25)',
                borderRadius: 12, padding: '16px 20px',
                marginBottom: 32,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: 14,
              }}>
                <div>
                  <div style={{ color: '#00D2C8', fontWeight: 700, fontSize: 15, marginBottom: 2 }}>
                    🎓 Gratulálunk, teljesítetted a kurzust!
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>
                    Töltsd le az elvégzési tanúsítványodat.
                  </div>
                </div>
                <button
                  onClick={downloadCertificate}
                  style={{
                    background: 'linear-gradient(135deg, #00D2C8, #8C50FF)',
                    color: '#fff', border: 'none', borderRadius: 8,
                    padding: '12px 22px', fontWeight: 700, fontSize: 14,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  Tanúsítvány letöltése
                </button>
              </div>
            )}

            {/* Activity / streak calendar */}
            <ActivityCalendar
              activityData={activityData}
              streakDays={streakDays}
              yearlyCount={yearlyCount}
            />

            {/* Start / continue button */}
            {allLessons.length > 0 && (
              <button
                onClick={() => {
                  const nextLesson = allLessons.find(l => !completedIds.includes(l.id)) || allLessons[0];
                  navigate(`/lecke/${nextLesson.id}`);
                }}
                style={{
                  background: '#00D2C8', color: '#07070b',
                  border: 'none', borderRadius: '10px', padding: '14px 28px',
                  fontWeight: 700, fontSize: '15px', cursor: 'pointer',
                }}
              >
                {completedCount > 0 ? 'Folytatás →' : 'Kezdés →'}
              </button>
            )}

            {/* Module lesson grids */}
            {modules.map(module => {
              if (module.locked) return <LockedModule key={module.id} module={module} session={session} resources={resourcesByModule[module.id]} />;
              const mDone = module.lessons.filter(l => completedIds.includes(l.id)).length;
              const mTotal = module.lessons.length;
              const mPct = mTotal ? Math.round((mDone / mTotal) * 100) : 0;
              const mComplete = mTotal > 0 && mPct === 100;
              return (
              <div key={module.id}>
                {/* Module section header */}
                <div style={{ marginBottom: 10, marginTop: 24 }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginBottom: 6, gap: 8,
                  }}>
                    <div style={{
                      fontSize: 12, fontWeight: 600,
                      color: mComplete ? '#1D9E75' : 'rgba(255,255,255,0.4)',
                      textTransform: 'uppercase', letterSpacing: '0.08em',
                      display: 'flex', alignItems: 'center', gap: 6, minWidth: 0,
                    }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {module.displayIndex}. {module.title}
                      </span>
                      {mComplete && <span style={{ flexShrink: 0 }}>✅</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {mDone}/{mTotal} lecke · {mPct}%
                    </div>
                  </div>
                  {/* Module progress bar */}
                  <div style={{ height: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 100, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${mPct}%`,
                      background: mComplete ? '#1D9E75' : '#00D2C8',
                      borderRadius: 100, transition: 'width 0.3s ease',
                    }} />
                  </div>
                </div>
                <div className="lesson-grid">
                  {module.lessons.map(lesson => {
                    const comingSoon = lesson.coming_soon === true;
                    return (
                    <div
                      key={lesson.id}
                      onClick={() => { if (!comingSoon) navigate(`/lecke/${lesson.id}`); }}
                      style={{
                        background: 'rgba(255,255,255,0.03)',
                        border: completedIds.includes(lesson.id)
                          ? '0.5px solid rgba(0,210,200,0.3)'
                          : '0.5px solid rgba(255,255,255,0.07)',
                        borderRadius: 10,
                        padding: '12px',
                        cursor: comingSoon ? 'default' : 'pointer',
                        opacity: comingSoon ? 0.45 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        transition: 'border-color 0.15s, background 0.15s',
                      }}
                      onMouseEnter={e => {
                        if (comingSoon) return;
                        e.currentTarget.style.borderColor = 'rgba(0,210,200,0.3)';
                        e.currentTarget.style.background = 'rgba(0,210,200,0.04)';
                      }}
                      onMouseLeave={e => {
                        if (comingSoon) return;
                        e.currentTarget.style.borderColor = completedIds.includes(lesson.id) ? 'rgba(0,210,200,0.3)' : 'rgba(255,255,255,0.07)';
                        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                      }}
                    >
                      {/* Thumbnail */}
                      <div style={{
                        width: 64, height: 42, borderRadius: 6, flexShrink: 0,
                        background: lesson.thumbnail_url ? 'transparent' : 'rgba(255,255,255,0.06)',
                        overflow: 'hidden', position: 'relative',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {lesson.thumbnail_url ? (
                          <img src={lesson.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,210,200,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="7" height="8" viewBox="0 0 7 8"><path d="M1 0.5l5 3.5-5 3.5z" fill="#0a0f1e"/></svg>
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                          <span className="lesson-title" style={{
                            fontSize: 12, fontWeight: 500,
                            color: completedIds.includes(lesson.id) ? '#00D2C8' : 'rgba(255,255,255,0.8)',
                          }}>
                            {lesson.title}
                          </span>
                          {comingSoon && (
                            <span style={{
                              fontSize: 10, color: '#fbbf24',
                              background: 'rgba(251,191,36,0.12)',
                              border: '0.5px solid rgba(251,191,36,0.3)',
                              borderRadius: 6, padding: '1px 6px',
                              whiteSpace: 'nowrap', flexShrink: 0,
                            }}>
                              ⏳ Hamarosan
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {comingSoon ? (
                            <span>Hamarosan elérhető</span>
                          ) : (
                            <>
                              {lesson.duration_minutes && <span>{lesson.duration_minutes} perc</span>}
                              {lesson.is_free_preview && <span style={{ color: '#00D2C8' }}>Ingyenes előnézet</span>}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Check circle */}
                      <div style={{
                        width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                        background: completedIds.includes(lesson.id) ? 'rgba(0,210,200,0.2)' : 'rgba(255,255,255,0.05)',
                        border: completedIds.includes(lesson.id) ? '1.5px solid #00D2C8' : '1px solid rgba(255,255,255,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {completedIds.includes(lesson.id) && (
                          <svg width="8" height="6" viewBox="0 0 8 6"><path d="M1 3l2 2 4-4" stroke="#00D2C8" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>

                {quizzesByModule[module.id] && (
                  <QuizSection
                    quiz={quizzesByModule[module.id]}
                    userId={session.user.id}
                    initialAttempt={attemptsByQuiz[quizzesByModule[module.id].id] || null}
                    onAttemptSaved={(quizId, a) =>
                      setAttemptsByQuiz(prev => ({ ...prev, [quizId]: a }))}
                  />
                )}
                <ModuleResources items={resourcesByModule[module.id]} session={session} />
                <GyakorloLink moduleId={module.id} />
                <AlakzatLink moduleId={module.id} />
              </div>
              );
            })}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CoursePage;
